/**
 * Year-boundary helpers. All operations work on ISO-8601 strings interpreted
 * as the user's LOCAL clock. The bear-trap rule (Lock 4) is:
 *
 *   "A withdrawal is restored on Jan 1 of the FOLLOWING year, in the user's
 *    local timezone."
 *
 * We never call new Date() with a UTC offset. The function takes the user's
 * local-clock ISO string verbatim and reads its year component. The caller
 * (engine code) is responsible for sourcing todayIso from the user's local
 * device clock; we do not introspect Intl.DateTimeFormat here because that
 * would couple the pure engine to a global side-effect.
 */

export function isInPriorYear(dateIso: string, todayIso: string): boolean {
  const dYear = parseYear(dateIso);
  const tYear = parseYear(todayIso);
  return dYear < tYear;
}

export function currentYearLocal(todayIso: string): number {
  return parseYear(todayIso);
}

function parseYear(iso: string): number {
  const match = iso.match(/^(\d{4})-/);
  if (!match) {
    throw new Error(`timezone.parseYear: invalid ISO string: ${iso}`);
  }
  return parseInt(match[1]!, 10);
}

/**
 * Returns the current local-clock time as an ISO-8601 string without Z suffix.
 * Use this (not new Date().toISOString()) whenever you need to pass today's
 * date to engine functions — the engine parses the year from the string
 * verbatim and must not see a UTC offset.
 */
export function localIsoNow(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}
